<section *ngIf="panelVisible" class="een-customer-account-panel" data-testid="een-customer-account-panel"
         [attr.data-readonly]="readOnly ? 'true' : null">
    <header class="een-customer-account-panel__header" *ngIf="!hidePanelHeading">
        <div>
            <h2 class="een-customer-account-panel__title">EEN Customer Account</h2>
            <p class="een-customer-account-panel__intro">
                <ng-container *ngIf="readOnly; else editableIntro">
                    Assigned EEN customer account used by this organization for vision provider credentials.
                    Changes are managed on the EEN Account tab.
                </ng-container>
                <ng-template #editableIntro>
                    Select which EEN customer account this organization uses for vision provider credentials.
                </ng-template>
            </p>
        </div>
        <div class="een-customer-account-panel__env" *ngIf="environment">
            <span class="env-label">Current Environment</span>
            <span class="env-badge">{{ formatEnvironmentLabel(environment) }}</span>
        </div>
    </header>
    <div class="een-customer-account-panel__env een-customer-account-panel__env--standalone"
         *ngIf="hidePanelHeading && environment">
        <span class="env-label">Current Environment</span>
        <span class="env-badge">{{ formatEnvironmentLabel(environment) }}</span>
    </div>
    <p class="een-customer-account-panel__intro" *ngIf="hidePanelHeading">
        <ng-container *ngIf="readOnly; else editableIntroStandalone">
            Assigned EEN customer account used by this organization for vision provider credentials.
            Changes are managed on the EEN Account tab.
        </ng-container>
        <ng-template #editableIntroStandalone>
            Select which EEN customer account this organization uses for vision provider credentials.
        </ng-template>
    </p>

    <div *ngIf="loading" class="een-customer-account-panel__loading" data-testid="een-customer-account-loading">
        Loading EEN customer account mapping…
    </div>

    <div *ngIf="!loading && loadError" class="een-customer-account-panel__error" data-testid="een-customer-account-error">
        {{ loadError }}
    </div>

    <ng-container *ngIf="!loading && !loadError">
        <!-- Read-only Operational Readiness display -->
        <ng-container *ngIf="readOnly">
            <div
                    *ngIf="staleMappingWarning"
                    class="een-customer-account-panel__error"
                    data-testid="een-customer-account-stale-warning"
                    role="alert">
                {{ staleMappingWarning }}
            </div>

            <div *ngIf="!currentEenAccountId && !hasRegisteredAccounts"
                 class="empty-state"
                 data-testid="een-customer-account-empty">
                <p>No EEN customer accounts are registered in KYRA for this environment.</p>
                <p class="helper-text">
                    Connect EEN from the Vision Provider Authentication page to register customer accounts,
                    then assign one on the
                    <a *ngIf="eenAccountTabLink" [routerLink]="eenAccountTabLink"
                       data-testid="een-customer-account-manage-link">EEN Account</a>
                    <ng-container *ngIf="!eenAccountTabLink">EEN Account</ng-container>
                    tab.
                </p>
            </div>

            <div *ngIf="!currentEenAccountId && hasRegisteredAccounts"
                 class="empty-state"
                 data-testid="een-customer-account-unassigned">
                <p>No EEN customer account is assigned to this organization.</p>
                <p class="helper-text" *ngIf="eenAccountTabLink">
                    Assign an active end-user account on the
                    <a [routerLink]="eenAccountTabLink"
                       data-testid="een-customer-account-manage-link">EEN Account</a>
                    tab.
                </p>
            </div>

            <div *ngIf="currentEenAccountId"
                 class="een-customer-account-readonly"
                 data-testid="een-customer-account-readonly">
                <dl class="een-customer-account-readonly__details">
                    <div class="een-customer-account-readonly__row">
                        <dt>Account</dt>
                        <dd data-testid="een-customer-account-readonly-label">{{ assignedAccountLabel }}</dd>
                    </div>
                    <div class="een-customer-account-readonly__row">
                        <dt>EEN account ID</dt>
                        <dd class="mono" data-testid="een-customer-account-readonly-id">{{ currentEenAccountId }}</dd>
                    </div>
                    <div class="een-customer-account-readonly__row">
                        <dt>Status</dt>
                        <dd data-testid="een-customer-account-readonly-status">{{ assignedAccountStatusLabel }}</dd>
                    </div>
                    <div class="een-customer-account-readonly__row" *ngIf="assignedAuthorizationStatusLabel">
                        <dt>Authorization</dt>
                        <dd data-testid="een-customer-account-readonly-auth-status">
                            {{ assignedAuthorizationStatusLabel }}
                        </dd>
                    </div>
                </dl>
                <p class="helper-text" *ngIf="eenAccountTabLink">
                    To change this assignment, use the
                    <a [routerLink]="eenAccountTabLink"
                       data-testid="een-customer-account-manage-link">EEN Account</a>
                    tab.
                </p>
            </div>
        </ng-container>

        <!-- Editable EEN Account tab -->
        <ng-container *ngIf="!readOnly">
            <div *ngIf="accounts.length === 0" class="empty-state" data-testid="een-customer-account-empty">
                <p>No EEN customer accounts are registered in KYRA for this environment.</p>
                <p class="helper-text">
                    Connect EEN from the Vision Provider Authentication page to register customer accounts.
                </p>
            </div>

            <div *ngIf="accounts.length > 0" class="configured-state" data-testid="een-customer-account-form">
                <div
                        *ngIf="staleMappingWarning"
                        class="een-customer-account-panel__error"
                        data-testid="een-customer-account-stale-warning">
                    {{ staleMappingWarning }}
                </div>
                <div class="form-group">
                    <label for="eenCustomerAccountSelect">EEN Customer Account</label>
                    <select
                            id="eenCustomerAccountSelect"
                            name="eenCustomerAccount"
                            [(ngModel)]="selectedEenAccountId"
                            [disabled]="saving"
                            data-testid="een-customer-account-select">
                        <option [ngValue]="noneValue">None / Not assigned</option>
                        <option *ngFor="let account of accounts" [ngValue]="account.een_account_id">
                            {{ accountLabel(account) }}
                        </option>
                    </select>
                </div>

                <div class="button-row">
                    <button
                            class="btn-save"
                            type="button"
                            (click)="onSave()"
                            [disabled]="saveDisabled"
                            data-testid="een-customer-account-save">
                        <fa-icon [icon]="['fas', 'square-plus']" style="padding-right: 3px;"></fa-icon>
                        {{ saving ? 'Saving…' : 'Save' }}
                    </button>
                </div>
            </div>
        </ng-container>
    </ng-container>
</section>
